/**
 * useTheme — antd ConfigProvider 테마 설정 훅 (themeStore 연동).
 *
 * 【역할】
 * - mode(light/dark), primaryColor, compact, fontSize, preset 을 themeStore 에서 읽어
 *   antd ConfigProvider 가 이해하는 ThemeConfig + componentDefaults 로 조립.
 *
 * 【주요 기능】
 * - themeConfig: ConfigProvider `theme` prop 으로 전달
 * - componentDefaults: ConfigProvider 나머지 글로벌 옵션 (componentSize/form/input/select)
 * - toggleDark(): light ↔ dark 토글
 * - setMode/setPrimaryColor/setCompact/setFontSize/applyPreset: 개별 setter 패스스루
 *
 * 【내부 상태】
 * - Zustand `themeStore` 에서 mode/primaryColor/compact/fontSize/preset 구독.
 * - useMemo 로 themeConfig 는 의존성 변경 시에만 재계산 → 불필요한 re-render 방지.
 *
 * 【사용 예시】
 * ```tsx
 * const { themeConfig, componentDefaults } = useTheme()
 * <ConfigProvider theme={themeConfig} {...componentDefaults}>{children}</ConfigProvider>
 * ```
 *
 * 【왜 이 패턴?】
 * - antd ThemeConfig 조립 로직을 페이지 코드에서 분리 + 사용자 preference (preset/fontSize) 중앙화.
 */
import { useCallback, useMemo } from 'react'
import { theme as antdTheme, ThemeConfig, ConfigProviderProps } from 'antd'
import { useThemeStore, THEME_PRESETS, type ThemePresetKey } from '@/store/themeStore'
import { THEME_SKINS, type ThemeSkinKey } from '@/design-skins'
import { useLabel } from './useLabel'

/**
 * useTheme — 테마 상태 + antd ConfigProvider 설정 객체 반환.
 */
export function useTheme() {
  const mode = useThemeStore((s) => s.mode)
  const setMode = useThemeStore((s) => s.setMode)
  const primaryColor = useThemeStore((s) => s.primaryColor)
  const setPrimaryColor = useThemeStore((s) => s.setPrimaryColor)
  const compact = useThemeStore((s) => s.compact)
  const setCompact = useThemeStore((s) => s.setCompact)
  const fontSize = useThemeStore((s) => s.fontSize)
  const setFontSize = useThemeStore((s) => s.setFontSize)
  const preset = useThemeStore((s) => s.preset)
  const applyPreset = useThemeStore((s) => s.applyPreset)
  const skin = useThemeStore((s) => s.skin)
  const setSkin = useThemeStore((s) => s.setSkin)

  const { t } = useLabel()

  /** light ↔ dark 토글 단축 함수. */
  const toggleDark = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark')
  }, [mode, setMode])

  const themeConfig = useMemo<ThemeConfig>(() => {
    const skinDef = THEME_SKINS[skin]
    const skinCompact = skinDef?.extraAlgorithm === 'compact'
    const algorithms = [
      mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      compact || skinCompact ? antdTheme.compactAlgorithm : null
    ].filter(Boolean) as ThemeConfig['algorithm'][]
    const presetExtra = preset !== 'custom' ? (THEME_PRESETS[preset]?.extraToken ?? {}) : {}
    // 스킨 token 에서 colorPrimary 는 제거 — primaryColor 는 스킨과 직교로 보존
    const { colorPrimary: _skinPrimary, ...skinTokenSafe } = (skinDef?.token ?? {}) as {
      colorPrimary?: string
      [k: string]: unknown
    }
    void _skinPrimary
    const skinComponents = skinDef?.componentDefaults ?? {}
    return {
      algorithm: algorithms as unknown as ThemeConfig['algorithm'],
      token: {
        borderRadius: 6,
        fontSize,
        ...presetExtra,
        ...skinTokenSafe,
        colorPrimary: primaryColor
      },
      components: {
        Table: {
          headerBg: undefined,
          cellPaddingBlockSM: 6,
          cellPaddingInlineSM: 8
        },
        Form: {
          itemMarginBottom: 12
        },
        ...skinComponents
      }
    }
  }, [mode, primaryColor, compact, fontSize, preset, skin])

  /** ConfigProvider 에 전역 컴포넌트 기본값 주입용 props */
  const componentDefaults = useMemo<Partial<ConfigProviderProps>>(
    () => ({
      componentSize: compact ? 'small' : 'middle',
      form: {
        scrollToFirstError: true,
        requiredMark: true,
        validateMessages: {
          required: t('LBL_FORM_REQUIRED', '${label} is required.'),
          types: {
            email: t('LBL_FORM_EMAIL', 'Invalid email format.'),
            number: t('LBL_FORM_NUMBER', 'Must be a number.')
          }
        }
      },
      input: { autoComplete: 'off' },
      select: { showSearch: true }
    }),
    [compact, t]
  )

  return {
    mode,
    setMode,
    toggleDark,
    primaryColor,
    setPrimaryColor,
    compact,
    setCompact,
    fontSize,
    setFontSize,
    preset,
    applyPreset,
    presets: THEME_PRESETS,
    skin,
    setSkin,
    skins: THEME_SKINS,
    themeConfig,
    componentDefaults
  }
}

export type { ThemePresetKey, ThemeSkinKey }
